import { TOLERANCE, type Um } from './units.js';
import type { Axis, Direction, EdgeId, GrainDirection, MaterialSlot, PanelPart, PanelRole, Vec3 } from './model.js';

const AXIS_INDEX: Record<Axis, 0 | 1 | 2> = { x: 0, y: 1, z: 2 };
const NEG: Record<Axis, Direction> = { x: 'left', y: 'bottom', z: 'back' };
const POS: Record<Axis, Direction> = { x: 'right', y: 'top', z: 'front' };

export interface Box {
  min: Vec3;
  size: Vec3;
}

export interface PanelSpec {
  cabinetId: string;
  localKey: string;
  role: PanelRole;
  label: string;
  box: Box;
  lengthAxis: Axis;
  widthAxis: Axis;
  materialSlot: MaterialSlot;
  grain?: GrainDirection;
  inCutList?: boolean;
  movable?: PanelPart['movable'];
  generic?: boolean;
}

function thirdAxis(a: Axis, b: Axis): Axis {
  const all: Axis[] = ['x', 'y', 'z'];
  const rest = all.filter((v) => v !== a && v !== b);
  if (rest.length !== 1) throw new Error(`Axes must differ: ${a}, ${b}`);
  return rest[0]!;
}

export function makePanel(spec: PanelSpec): PanelPart {
  const thicknessAxis = thirdAxis(spec.lengthAxis, spec.widthAxis);
  const length = spec.box.size[AXIS_INDEX[spec.lengthAxis]];
  const width = spec.box.size[AXIS_INDEX[spec.widthAxis]];
  const thickness = spec.box.size[AXIS_INDEX[thicknessAxis]];
  const edges = {
    L1: { id: 'L1' as const, length, facing: NEG[spec.widthAxis], contacts: [] },
    L2: { id: 'L2' as const, length, facing: POS[spec.widthAxis], contacts: [] },
    W1: { id: 'W1' as const, length: width, facing: NEG[spec.lengthAxis], contacts: [] },
    W2: { id: 'W2' as const, length: width, facing: POS[spec.lengthAxis], contacts: [] },
  };
  return {
    id: `${spec.cabinetId}/${spec.localKey}`,
    localKey: spec.localKey,
    cabinetId: spec.cabinetId,
    role: spec.role,
    label: spec.label,
    length,
    width,
    thickness,
    materialSlot: spec.materialSlot,
    grain: spec.grain ?? 'length',
    placement: { min: spec.box.min, lengthAxis: spec.lengthAxis, widthAxis: spec.widthAxis, thicknessAxis },
    edges,
    connections: [],
    inCutList: spec.inCutList ?? true,
    ...(spec.movable ? { movable: spec.movable } : {}),
    ...(spec.generic ? { generic: true } : {}),
  };
}

export function partBox(part: PanelPart): Box {
  const size: Vec3 = [0, 0, 0];
  size[AXIS_INDEX[part.placement.lengthAxis]] = part.length;
  size[AXIS_INDEX[part.placement.widthAxis]] = part.width;
  size[AXIS_INDEX[part.placement.thicknessAxis]] = part.thickness;
  return { min: part.placement.min, size };
}

interface Face {
  axis: 0 | 1 | 2; // normal axis
  coord: Um; // plane coordinate
  sign: -1 | 1; // outward normal
  min: [Um, Um]; // extents on the two remaining axes (ascending axis order)
  max: [Um, Um];
}

function otherAxes(axis: 0 | 1 | 2): [0 | 1 | 2, 0 | 1 | 2] {
  return axis === 0 ? [1, 2] : axis === 1 ? [0, 2] : [0, 1];
}

function boxFace(box: Box, axis: 0 | 1 | 2, sign: -1 | 1): Face {
  const [a, b] = otherAxes(axis);
  const coord = sign < 0 ? box.min[axis] : box.min[axis] + box.size[axis];
  return {
    axis,
    coord,
    sign,
    min: [box.min[a], box.min[b]],
    max: [box.min[a] + box.size[a], box.min[b] + box.size[b]],
  };
}

function edgeFace(part: PanelPart, edge: EdgeId): Face {
  const box = partBox(part);
  const along = edge === 'L1' || edge === 'L2' ? part.placement.widthAxis : part.placement.lengthAxis;
  const sign: -1 | 1 = edge === 'L1' || edge === 'W1' ? -1 : 1;
  return boxFace(box, AXIS_INDEX[along], sign);
}

function overlapArea(f: Face, g: Face): number {
  const w = Math.min(f.max[0], g.max[0]) - Math.max(f.min[0], g.min[0]);
  const h = Math.min(f.max[1], g.max[1]) - Math.max(f.min[1], g.min[1]);
  return w > 0 && h > 0 ? w * h : 0;
}

export interface EdgeCoverage {
  partId: string;
  edge: EdgeId;
  contacts: { partId: string; fraction: number; gap: Um }[];
}

/**
 * Contact-based coverage of each panel edge face by faces of other parts in the same frame.
 * Uses face planes within TOLERANCE.contact and real overlap area, not bounding-box touching.
 * Faces up to `maxGap` away are reported with their gap so policies can treat small clearances
 * (e.g. loose shelves) as enclosed. Assumes covering parts do not overlap each other.
 */
export function computeEdgeCoverage(parts: PanelPart[], maxGap: Um = TOLERANCE.contact): EdgeCoverage[] {
  const out: EdgeCoverage[] = [];
  const boxes = parts.map((p) => ({ part: p, box: partBox(p) }));
  for (const { part } of boxes) {
    for (const edge of ['L1', 'L2', 'W1', 'W2'] as const) {
      const face = edgeFace(part, edge);
      const area = (face.max[0] - face.min[0]) * (face.max[1] - face.min[1]);
      const contacts: { partId: string; fraction: number; gap: Um }[] = [];
      for (const other of boxes) {
        if (other.part.id === part.id) continue;
        const opposite = boxFace(other.box, face.axis, (-face.sign) as -1 | 1);
        // Signed distance along the outward normal: negative = slight interpenetration.
        const gap = (opposite.coord - face.coord) * face.sign;
        if (gap < -TOLERANCE.contact || gap > maxGap) continue;
        const a = overlapArea(face, opposite);
        if (a > 0 && area > 0) contacts.push({ partId: other.part.id, fraction: a / area, gap: Math.max(0, gap) });
      }
      out.push({ partId: part.id, edge, contacts });
    }
  }
  return out;
}

/** Overlap volume test between two boxes; returns penetration depth on the shallowest axis. */
export function boxPenetration(a: Box, b: Box): number {
  let depth = Infinity;
  for (const i of [0, 1, 2] as const) {
    const d = Math.min(a.min[i] + a.size[i], b.min[i] + b.size[i]) - Math.max(a.min[i], b.min[i]);
    if (d <= 0) return 0;
    depth = Math.min(depth, d);
  }
  return depth;
}
